/**
 * The Property, Capital, Owners and Loans sheets: everything the client sent
 * besides the P&L, one row per house (or loan), in their own column names.
 *
 * - **Property**: the export's Property Master, Property Features and the
 *   finishes from Capital Master, grouped under those names. Columns no house
 *   fills are hidden and counted, not drawn empty.
 * - **Capital**: houses down, components across, each cell the component's
 *   age, highlighted once it is past its expected life, with the count and
 *   the replacement cost due at the end of the row.
 * - **Owners** and **Loans**: the rows as they arrived.
 *
 * Every cell opens the record panel with the workbook cell it came from.
 */

import { ChevronRight } from "lucide-react";
import React from "react";
import type {
	Portfolio,
	PortfolioComponent,
	PortfolioHouse,
} from "../../services/portfolioDataService";
import { formatMoney } from "./portfolioModel";
import {
	type RecordSelection,
	componentLabel,
	formatField,
	headerLabel,
	shownFields,
	sourceRowOf,
} from "./sheets";

export interface DataSheetProps {
	portfolio: Portfolio;
	houses: readonly PortfolioHouse[];
	selectedKey: string | null;
	onSelect: (record: RecordSelection) => void;
}

const FRAME = "overflow-x-auto rounded-xl border border-gray-200 bg-white";
const TH =
	"whitespace-nowrap border-b border-gray-200 bg-[#fafafa] px-3 py-2 text-left text-[12.5px] font-semibold text-[#1F2937]";
const GROUP_TH =
	"whitespace-nowrap border-b border-l border-gray-200 bg-white px-3 pb-1 pt-2 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7280] first:border-l-0";
const STICKY = "sticky left-0 z-[1] bg-white";
const CELL_BUTTON =
	"block w-full whitespace-nowrap px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#008782] hover:bg-[#f5f8ff]";
const SELECTED = "bg-[#eef4ff] font-semibold shadow-[inset_0_0_0_2px_#444ce7]";

const HiddenNote: React.FC<{ count: number; what: string }> = ({ count, what }) =>
	count > 0 ? (
		<p className="text-xs text-[#6B7280]">
			{count} {what} {count === 1 ? "is" : "are"} blank for every house in your export and
			{count === 1 ? " is" : " are"} hidden. Needs you lists them.
		</p>
	) : null;

export const PropertySheet: React.FC<DataSheetProps> = ({ portfolio, houses, selectedKey, onSelect }) => {
	const spec = portfolio.manifest.property_sheet;
	const { shown, hidden } = shownFields(portfolio, [
		"Property Master",
		"Property Features",
		"Capital Master",
	]);
	// Columns the first column already says, or that every row repeats.
	const fields = shown.filter((f) => !["plat_id", "plat_name", "address"].includes(f.key));
	const groups = fields.reduce<{ name: string; span: number }[]>((out, field) => {
		const label = field.group === "Capital Master" ? "Capital Master · finishes" : field.group;
		const last = out[out.length - 1];
		if (last && last.name === label) last.span += 1;
		else out.push({ name: label, span: 1 });
		return out;
	}, []);
	if (!spec) return null;
	return (
		<div className="flex flex-col gap-2">
			<div className={FRAME}>
				<table className="w-full border-collapse text-[13px] tabular-nums">
					<thead>
						<tr>
							<th className={`${GROUP_TH} ${STICKY}`} />
							{groups.map((group) => (
								<th key={group.name} colSpan={group.span} className={GROUP_TH}>
									{group.name}
								</th>
							))}
						</tr>
						<tr>
							<th scope="col" className={`${TH} ${STICKY} bg-[#fafafa]`}>
								House
							</th>
							{fields.map((field) => (
								<th key={field.key} scope="col" className={TH} title={`Column ${field.column}`}>
									{headerLabel(field.header)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{houses.map((house) => {
							const row = sourceRowOf(house);
							return (
								<tr key={house.house_id}>
									<td
										className={`${STICKY} whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 font-semibold text-[#111111]`}
										title={String(house.address ?? "")}
									>
										{house.house_id}
									</td>
									{fields.map((field) => {
										const value = formatField(house[field.key], field.key);
										const key = `property:${house.house_id}:${field.key}`;
										return (
											<td key={field.key} className="border-b border-[#f1f2f4] p-0 text-[#1F2937]">
												<button
													type="button"
													disabled={row === null}
													aria-pressed={selectedKey === key}
													onClick={() =>
														onSelect({
															key,
															houseId: house.house_id,
															title: headerLabel(field.header),
															value,
															sourceFile: spec.file,
															sheet: spec.sheet,
															cell: `${field.column}${row}`,
															header: field.header,
															note:
																field.group === "Property Features"
																	? "A feature score as entered in your Property Analysis."
																	: undefined,
														})
													}
													className={`${CELL_BUTTON} ${selectedKey === key ? SELECTED : ""}`}
												>
													{value || <span className="text-[#c4c7cc]">—</span>}
												</button>
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<HiddenNote count={hidden.length} what={hidden.length === 1 ? "column" : "columns"} />
		</div>
	);
};

const pastLife = (item: PortfolioComponent) => (item.years_past_life ?? 0) > 0;

const costRange = (low: number, high: number) =>
	low === high ? `$${formatMoney(low)}` : `$${formatMoney(low)}–${formatMoney(high)}`;

export const CapitalSheet: React.FC<DataSheetProps> = ({ portfolio, houses, selectedKey, onSelect }) => {
	const spec = portfolio.manifest.property_sheet;
	const items = portfolio.tables.capital_component;
	const present = new Set(items.map((item) => item.component));
	const components = (spec?.components ?? []).filter((c) => present.has(c.key));
	const byHouse = new Map<string, Map<string, PortfolioComponent>>();
	for (const item of items) {
		const row = byHouse.get(item.house_id) ?? new Map();
		row.set(item.component, item);
		byHouse.set(item.house_id, row);
	}
	const shownHouses = houses.filter((house) => byHouse.has(house.house_id));
	const overdue = items.filter(
		(item) => pastLife(item) && shownHouses.some((h) => h.house_id === item.house_id),
	);
	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-wrap items-center gap-4 text-xs text-[#5B6472]">
				<span>Each cell is the item's age in years.</span>
				<span className="inline-flex items-center gap-1.5">
					<span aria-hidden className="inline-block h-3 w-3 rounded-sm bg-[#fdecd2]" />
					Past its expected life ({overdue.length} items)
				</span>
				<span>Life and cost come from your column headers, e.g. “Roof_Age (14 yrs,$6500 - $7500)”.</span>
			</div>
			<div className={FRAME}>
				<table className="w-full border-collapse text-[13px] tabular-nums">
					<thead>
						<tr>
							<th scope="col" className={`${TH} ${STICKY} bg-[#fafafa]`}>
								House
							</th>
							{components.map((component) => (
								<th key={component.key} scope="col" className={`${TH} text-right`}>
									<span className="block">{componentLabel(component.key)}</span>
									<span className="block text-[11px] font-normal text-[#6B7280]">{component.rule}</span>
								</th>
							))}
							<th scope="col" className={`${TH} text-right`}>Past life</th>
							<th scope="col" className={`${TH} text-right`}>Replacement due</th>
						</tr>
					</thead>
					<tbody>
						{shownHouses.map((house) => {
							const row = byHouse.get(house.house_id) ?? new Map<string, PortfolioComponent>();
							const due = [...row.values()].filter(pastLife);
							const low = due.reduce((sum, item) => sum + (item.replacement_cost_low ?? 0), 0);
							const high = due.reduce((sum, item) => sum + (item.replacement_cost_high ?? 0), 0);
							return (
								<tr key={house.house_id}>
									<td className={`${STICKY} whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 font-semibold text-[#111111]`}>
										{house.house_id}
									</td>
									{components.map((component) => {
										const item = row.get(component.key);
										if (!item) {
											return (
												<td key={component.key} className="border-b border-[#f1f2f4] px-3 py-2 text-right text-[#c4c7cc]">
													—
												</td>
											);
										}
										const key = `capital:${house.house_id}:${component.key}`;
										const late = pastLife(item);
										const life =
											item.life_years_low === item.life_years_high
												? `${item.life_years_high} yrs`
												: `${item.life_years_low}–${item.life_years_high} yrs`;
										return (
											<td key={component.key} className="border-b border-[#f1f2f4] p-0">
												<button
													type="button"
													aria-pressed={selectedKey === key}
													onClick={() =>
														onSelect({
															key,
															houseId: house.house_id,
															title: componentLabel(component.key),
															value: `${item.age_years ?? "—"} years old`,
															sourceFile: item.source_file,
															sheet: item.sheet,
															cell: item.age_cell,
															rows: [
																{ label: `Installed (${item.year_cell})`, value: String(item.installed_year ?? "—") },
																{ label: "Expected life", value: life },
																...(late
																	? [{ label: "Past its life by", value: `${item.years_past_life} yrs`, tone: "attention" as const }]
																	: [{ label: "Life left", value: `${Math.abs(item.years_past_life ?? 0)} yrs` }]),
																{
																	label: "Replacement cost",
																	value: costRange(item.replacement_cost_low ?? 0, item.replacement_cost_high ?? 0),
																},
															],
															note: `Rule from the column header: “${item.rule_text}”.`,
														})
													}
													className={`${CELL_BUTTON} text-right ${late ? "bg-[#fdecd2] font-semibold text-[#93370d] hover:bg-[#fbdcb3]" : "text-[#1F2937]"} ${selectedKey === key ? SELECTED : ""}`}
												>
													{item.age_years ?? "—"}
												</button>
											</td>
										);
									})}
									<td className={`border-b border-[#f1f2f4] px-3 py-2 text-right ${due.length ? "font-semibold text-[#b54708]" : "text-[#6B7280]"}`}>
										{due.length}
									</td>
									<td className="whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 text-right text-[#1F2937]">
										{due.length ? costRange(low, high) : "—"}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export const OwnersSheet: React.FC<DataSheetProps> = ({ portfolio, houses, selectedKey, onSelect }) => {
	const spec = portfolio.manifest.property_sheet;
	const { shown } = shownFields(portfolio, ["Owner Master"]);
	const rowsOf = new Map(houses.map((house) => [house.house_id, sourceRowOf(house)]));
	const owners = portfolio.tables.owner.filter((owner) => rowsOf.has(String(owner.house_id)));
	if (!spec) return null;
	return (
		<div className="flex flex-col gap-2">
			<div className={FRAME}>
				<table className="w-full border-collapse text-[13px] tabular-nums">
					<thead>
						<tr>
							<th scope="col" className={TH}>House</th>
							{shown.map((field) => (
								<th key={field.key} scope="col" className={TH}>
									{headerLabel(field.header)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{owners.map((owner) => {
							const houseId = String(owner.house_id);
							const row = rowsOf.get(houseId);
							return (
								<tr key={String(owner.ownership_id)}>
									<td className="whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 font-semibold text-[#111111]">
										{houseId}
									</td>
									{shown.map((field) => {
										const key = `owner:${houseId}:${field.key}`;
										const value = formatField(owner[field.key], field.key);
										return (
											<td key={field.key} className="border-b border-[#f1f2f4] p-0 text-[#1F2937]">
												<button
													type="button"
													aria-pressed={selectedKey === key}
													onClick={() =>
														onSelect({
															key,
															houseId,
															title: headerLabel(field.header),
															value,
															sourceFile: spec.file,
															sheet: spec.sheet,
															cell: `${field.column}${row}`,
															header: field.header,
														})
													}
													className={`${CELL_BUTTON} ${selectedKey === key ? SELECTED : ""}`}
												>
													{value}
												</button>
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<p className="text-xs text-[#6B7280]">
				Your export has one owner row per house, so the family distribution split is not here yet.
			</p>
		</div>
	);
};

/** The loan lines every house carries, each its share of the plat loan. */
const LOAN_LINES: { key: string; label: string; unit: string }[] = [
	{ key: "loan_balance", label: "Loan balance", unit: "$" },
	{ key: "interest_expense", label: "Interest", unit: "$ / yr" },
	{ key: "principal_paid", label: "Principal", unit: "$ / yr" },
];

/**
 * Loans at house level, like the other sheets: each plat's loan as a group
 * header with its totals, then every house's share of it. The workbook
 * divides one plat loan evenly, so the shares are the same within a plat and
 * say nothing about which house is more leveraged.
 */
export interface LoansSheetProps extends DataSheetProps {
	year: number;
	/**
	 * On the portfolio view: which plats show their houses' shares under the
	 * loan row. Absent (one plat open), every house is shown.
	 */
	expanded?: ReadonlySet<string>;
	onToggle?: (platId: string) => void;
	onOpenPlat?: (platId: string) => void;
}

export const LoansSheet: React.FC<LoansSheetProps> = ({
	portfolio,
	houses,
	selectedKey,
	onSelect,
	year,
	expanded,
	onToggle,
	onOpenPlat,
}) => {
	const collapsible = expanded !== undefined && onToggle !== undefined;
	const facts = new Map(
		portfolio.tables.fact
			.filter((f) => f.year === year)
			.map((f) => [`${f.house_id}:${f.line_item}`, f]),
	);
	const shown = new Set(houses.map((house) => house.house_id));
	// The rate is entered per house in the workbook, but it is the plat loan's
	// rate, so it is said once on the plat row: which rates, on how many houses.
	const rateSpread = (platId: string): string => {
		const counts = new Map<string, number>();
		for (const fact of portfolio.tables.fact) {
			if (fact.year !== year || fact.plat_id !== platId || fact.line_item !== "interest_rate") continue;
			if (fact.value === null) continue;
			const label = `${Number((fact.value * 100).toFixed(3))}%`;
			counts.set(label, (counts.get(label) ?? 0) + 1);
		}
		const entries = [...counts.entries()];
		if (entries.length <= 1) return entries[0] ? `Rate ${entries[0][0]}` : "";
		return `Rate entered: ${entries.map(([rate, n]) => `${rate} on ${n} houses`).join(", ")}`;
	};
	const loans = portfolio.tables.loan.filter((loan) =>
		houses.some((house) => house.plat_id === loan.plat_id),
	);
	return (
		<div className="flex flex-col gap-2">
			<div className={FRAME}>
				<table className="w-full border-collapse text-[13px] tabular-nums">
					<thead>
						<tr>
							<th scope="col" className={`${TH} ${STICKY} bg-[#fafafa]`}>
								{collapsible ? "Plat loan / house" : "House"}
							</th>
							{LOAN_LINES.map((line) => (
								<th key={line.key} scope="col" className={`${TH} text-right`}>
									<span className="block">{line.label}</span>
									<span className="block text-[11px] font-normal text-[#6B7280]">{line.unit} · split</span>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{loans.map((loan) => {
							const rates = loan.interest_rates_entered.split("/").filter(Boolean);
							const open = !collapsible || Boolean(expanded?.has(loan.plat_id));
							const plat = open
								? houses.filter((house) => house.plat_id === loan.plat_id && shown.has(house.house_id))
								: [];
							return (
								<React.Fragment key={loan.plat_id}>
									<tr
										onClick={collapsible && onOpenPlat ? () => onOpenPlat(loan.plat_id) : undefined}
										className={`bg-[#fafafa] font-semibold ${collapsible ? "cursor-pointer hover:bg-[#f0f7f6]" : ""}`}
									>
										<td className={`${STICKY} border-b border-gray-200 bg-inherit px-3 py-2 text-[#111111]`}>
											<span className="flex items-center gap-1.5 whitespace-nowrap">
												{collapsible && (
													<button
														type="button"
														onClick={(event) => {
															event.stopPropagation();
															onToggle?.(loan.plat_id);
														}}
														aria-expanded={open}
														aria-label={`${open ? "Hide" : "Show"} ${loan.plat_name}'s houses here`}
														className="-ml-1 inline-flex h-5 w-5 items-center justify-center rounded text-[#5B6472] hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
													>
														<ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} aria-hidden />
													</button>
												)}
												{loan.plat_name} loan · {loan.bank}
											</span>
											<span className="block text-xs font-normal text-[#5B6472]">
												{rateSpread(loan.plat_id)}
												{rates.length > 1 && (
													<span className="ml-2 inline-flex h-[18px] items-center rounded-full bg-[#fffaeb] px-2 text-[11px] font-semibold text-[#b54708]">
														Two rates
													</span>
												)}
											</span>
											<span className="block text-xs font-normal text-[#6B7280]">
												Split evenly across {loan.house_count} houses
											</span>
										</td>
										<td className="border-b border-gray-200 px-3 py-2 text-right">{formatMoney(loan.loan_balance)}</td>
										<td className="border-b border-gray-200 px-3 py-2 text-right">{formatMoney(loan.interest_paid)}</td>
										<td className="border-b border-gray-200 px-3 py-2 text-right">{formatMoney(loan.principal_paid)}</td>
									</tr>
									{plat.map((house) => {
										return (
											<tr key={house.house_id}>
												<td className={`${STICKY} whitespace-nowrap border-b border-[#f1f2f4] px-3 py-2 font-semibold text-[#111111]`}>
													{house.house_id}
												</td>
												{LOAN_LINES.map((line) => {
													const fact = facts.get(`${house.house_id}:${line.key}`);
													const key = `loan:${house.house_id}:${line.key}`;
													const value = fact ? `$${formatMoney(fact.value)}` : "";
													return (
														<td key={line.key} className="border-b border-[#f1f2f4] p-0 text-[#6B7280]">
															<button
																type="button"
																disabled={!fact}
																aria-pressed={selectedKey === key}
																onClick={() =>
																	fact &&
																	onSelect({
																		key,
																		houseId: house.house_id,
																		title: `${line.label}, ${year}`,
																		value,
																		sourceFile: fact.source_file,
																		sheet: fact.sheet,
																		cell: fact.cell,
																		header: fact.source_label.trim(),
																		note: `This house's share of the ${loan.plat_name} loan: the plat amount divided evenly across its ${loan.house_count} houses. It does not show which house is more leveraged.`,
																	})
																}
																className={`${CELL_BUTTON} text-right ${selectedKey === key ? SELECTED : ""}`}
															>
																{fact ? formatMoney(fact.value) : "—"}
															</button>
														</td>
													);
												})}
											</tr>
										);
									})}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
			<p className="text-xs text-[#6B7280]">
				Read from the plat tabs of the finance workbook. A loan master would give each house its own loan.
			</p>
		</div>
	);
};
